import { Button, Input, Select, toast } from "@gotts.ai/ui";
import { useEffect, useMemo, useState } from "react";

import { MaxButton } from "../../components/MaxButton";
import { useEvmBackend } from "../../context";
import { useRpcRequest } from "../../hooks/useRpcRequest";
import { getAgentForAccountIndex } from "../../lib/constants";
import { formatEth as formatEthSmart } from "../../lib/format";

interface FundAccountPanelProps {
  onFunded: () => void;
  balances: Record<string, string>;
  selectedAccount?: string;
}

export function FundAccountPanel({
  onFunded,
  balances,
  selectedAccount,
}: FundAccountPanelProps) {
  const { request } = useRpcRequest();
  const { accounts } = useEvmBackend();

  const accountOptions = useMemo(
    () => [
      ...accounts.map((a, i) => {
        const agent = getAgentForAccountIndex(i);
        const agentSuffix = agent ? ` \u00b7 ${agent.name}` : "";
        return {
          label: `${a.label}${agentSuffix} (${a.address.slice(0, 6)}...${a.address.slice(-4)})`,
          value: a.address,
        };
      }),
      { label: "Other", value: "__other__" },
    ],
    [accounts],
  );

  const [accountSelect, setAccountSelect] = useState<string>(
    accounts[0]?.address ?? "",
  );
  const [customAddress, setCustomAddress] = useState("");
  const [fundAmount, setFundAmount] = useState("");
  const [fundLoading, setFundLoading] = useState(false);

  // Sync from parent selectedAccount prop
  useEffect(() => {
    if (selectedAccount && selectedAccount !== accountSelect) {
      setAccountSelect(selectedAccount);
    }
  }, [selectedAccount]);

  const fundAddress =
    accountSelect === "__other__" ? customAddress : accountSelect;

  const currentBalanceEth = useMemo(() => {
    if (!fundAddress || accountSelect === "__other__") return null;
    const hex = balances[fundAddress];
    if (!hex) return null;
    return Number(BigInt(hex)) / 1e18;
  }, [fundAddress, balances, accountSelect]);

  const currentBalanceDisplay = useMemo(() => {
    if (currentBalanceEth === null) return null;
    return formatEthSmart(currentBalanceEth);
  }, [currentBalanceEth]);

  async function handleFund() {
    if (!fundAddress || !fundAmount) return;
    setFundLoading(true);
    try {
      const addWei = BigInt(Math.floor(parseFloat(fundAmount) * 1e18));
      // Get current balance and add to it
      const currentHex = (await request("eth_getBalance", [
        fundAddress,
        "latest",
      ])) as string | undefined;
      const newWei = BigInt(currentHex ?? "0x0") + addWei;
      const newHex = "0x" + newWei.toString(16);
      await request("anvil_setBalance", [fundAddress, newHex]);
      await request("evm_mine", []);
      toast.success(
        `Funded ${fundAddress.slice(0, 6)}...${fundAddress.slice(-4)} with ${fundAmount} ETH`,
      );
      onFunded();
    } catch (err) {
      toast.error(
        `Fund failed: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setFundLoading(false);
    }
  }

  async function handleQuickFund(amount: string) {
    if (!fundAddress) return;
    setFundLoading(true);
    try {
      const addWei = BigInt(Math.floor(parseFloat(amount) * 1e18));
      // Get current balance and add to it
      const currentHex = (await request("eth_getBalance", [
        fundAddress,
        "latest",
      ])) as string | undefined;
      const newWei = BigInt(currentHex ?? "0x0") + addWei;
      const newHex = "0x" + newWei.toString(16);
      await request("anvil_setBalance", [fundAddress, newHex]);
      await request("evm_mine", []);
      toast.success(
        `Funded ${fundAddress.slice(0, 6)}...${fundAddress.slice(-4)} with ${amount} ETH`,
      );
      setFundAmount(amount);
      onFunded();
    } catch (err) {
      toast.error(
        `Fund failed: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setFundLoading(false);
    }
  }

  return (
    <div
      className="glass-card max-w-3xl p-[var(--spacing-card)]"
      data-testid="panel-Fund Account"
    >
      <div className="section-tag">Fund Account</div>
      <div className="space-y-2">
        {/* Main row: Select + Amount + Fund */}
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[240px] flex-1">
            <label className="section-label mb-1 block">To Account</label>
            <Select
              options={accountOptions}
              value={accountSelect}
              onValueChange={setAccountSelect}
              placeholder="Select account"
              className="w-full"
            />
          </div>
          {accountSelect === "__other__" && (
            <Input
              type="text"
              placeholder="0x... custom address"
              value={customAddress}
              onChange={(e) => {
                setCustomAddress(e.target.value);
              }}
              className="min-w-[200px] flex-1"
            />
          )}
          <div>
            {currentBalanceDisplay !== null && (
              <div className="mb-1 flex items-center gap-1.5">
                <span className="font-mono text-sm text-[var(--color-text)]">
                  {currentBalanceDisplay} ETH
                </span>
                <MaxButton
                  onClick={() => {
                    if (currentBalanceEth !== null)
                      setFundAmount(String(currentBalanceEth));
                  }}
                />
              </div>
            )}
            <div className="relative">
              <Input
                type="number"
                placeholder="Amount"
                value={fundAmount}
                onChange={(e) => {
                  setFundAmount(e.target.value);
                }}
                className="w-36 pr-10"
              />
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 font-mono text-xs text-[var(--color-text-muted)]">
                ETH
              </span>
            </div>
          </div>
          <Button
            variant="gold"
            size="sm"
            onClick={handleFund}
            loading={fundLoading}
            disabled={fundLoading || !fundAddress || !fundAmount}
          >
            Fund
          </Button>
        </div>
        {/* Preset fund buttons */}
        <div className="flex items-center gap-1">
          <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">
            Presets:
          </span>
          {["100", "1000", "10000"].map((amt) => (
            <button
              key={amt}
              className="btn-percentage-pill"
              onClick={() => handleQuickFund(amt)}
              disabled={!fundAddress || fundLoading}
            >
              {amt} ETH
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
